import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOrganization } from '../context/OrganizationContext';
import { BACKEND_URL } from '../config';
import { toast } from 'sonner';

import {
  ArrowSquareOut,
  ArrowsClockwise,
  CaretDown,
  CaretRight,
  Check,
  CheckCircle,
  CircleNotch,
  Clock,
  HardDrives,
  Info,
  Lightning,
  MagnifyingGlass,
  Plug,
  Plus,
  Trash,
  Warning,
  WarningCircle,
  XCircle
} from '@phosphor-icons/react';
/**
 * MCP Providers Component
 * Manage connections to external MCP servers
 */
const MCPProviders = () => {
  const { activeOrganization } = useOrganization();
  
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [refreshingProvider, setRefreshingProvider] = useState(null);
  const [testingProvider, setTestingProvider] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch providers
  const fetchProviders = useCallback(async () => {
    if (!activeOrganization?.organization_id) return;
    
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/integrations/mcp/providers?include_tools=true`,
        {
          credentials: 'include',
          headers: {
            'X-Organization-Id': activeOrganization.organization_id
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers || []);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to fetch providers');
      }
    } catch (err) {
      console.error('Error fetching providers:', err);
      toast.error('Failed to fetch providers');
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.organization_id]);

  useEffect(() => {
    if (activeOrganization) {
      fetchProviders();
    }
  }, [activeOrganization, fetchProviders]);

  // ArrowsClockwise provider tools
  const refreshProvider = async (providerId) => {
    setRefreshingProvider(providerId);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/integrations/mcp/providers/${providerId}/refresh`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-Organization-Id': activeOrganization.organization_id
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Refreshed: ${data.discovered_tools_count} tools found`);
        fetchProviders();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to refresh provider');
      }
    } catch (err) {
      toast.error('Failed to refresh provider');
    } finally {
      setRefreshingProvider(null);
    }
  };

  // Test provider connectivity with a specific safe tool
  const testProvider = async (providerId, toolName = 'mm.echo') => {
    setTestingProvider(providerId);
    // Clear previous result for this provider
    setTestResults(prev => ({ ...prev, [providerId]: null }));
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/integrations/mcp/providers/${providerId}/test`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Organization-Id': activeOrganization.organization_id
          },
          body: JSON.stringify({ tool_name: toolName })
        }
      );
      
      const data = await response.json();
      setTestResults(prev => ({ ...prev, [providerId]: data }));
      
      if (data.success) {
        toast.success(`Test passed: ${toolName} in ${data.latency_ms}ms`);
      } else {
        toast.error(`Test failed: ${data.error?.message || 'Unknown error'}`);
      }
    } catch (err) {
      setTestResults(prev => ({ 
        ...prev, 
        [providerId]: { success: false, error: { message: 'Network error' } }
      }));
      toast.error('Failed to test provider');
    } finally {
      setTestingProvider(null);
    }
  };

  // Trash provider
  const deleteProvider = async (providerId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/integrations/mcp/providers/${providerId}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'X-Organization-Id': activeOrganization.organization_id
          }
        }
      );
      
      if (response.ok) {
        toast.success('Provider disconnected');
        setProviders(providers.filter(p => p.id !== providerId));
        setShowDeleteModal(null);
        setSelectedProvider(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to disconnect provider');
      }
    } catch (err) {
      toast.error('Failed to disconnect provider');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!activeOrganization) {
    return (
      <div className="flex items-center justify-center p-8">
        <CircleNotch className="w-8 h-8 animate-spin text-[#6366F1]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Plug size={24} className="text-[#6366F1]" />
            MCP Servers
          </h2>
          <p className="text-sm text-[#A0AEC0] mt-1">
            Connect to external MCP servers to use their tools in your agents
          </p>
        </div>
        <button
          onClick={() => setShowConnectModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#5855E0] text-white rounded-lg transition-colors"
        >
          <Plus size={18} />
          Connect MCP HardDrives
        </button>
      </div>

      {/* Info Banner */}
      <div className="p-4 rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/30">
        <div className="flex items-start gap-3">
          <Lightning size={20} className="text-[#6366F1] mt-0.5" />
          <div>
            <p className="text-white font-medium">External MCP Integration</p>
            <p className="text-sm text-[#A0AEC0] mt-1">
              Connect to MCP servers like Zapier, Make, or custom servers to access their tools. 
              Your agents can then bind capabilities to these external tools.
            </p>
          </div>
        </div>
      </div>

      {/* Providers List */}
      {loading ? (
        <div className="p-8 text-center">
          <CircleNotch className="w-8 h-8 animate-spin text-[#6366F1] mx-auto" />
        </div>
      ) : providers.length === 0 ? (
        <div className="p-8 text-center rounded-xl border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.02)]">
          <HardDrives size={48} className="mx-auto mb-4 text-[#A0AEC0] opacity-50" />
          <p className="text-white font-medium">No MCP servers connected</p>
          <p className="text-sm text-[#A0AEC0] mt-1">
            Connect to external MCP servers to access their tools
          </p>
          <button
            onClick={() => setShowConnectModal(true)}
            className="mt-4 px-4 py-2 bg-[#6366F1] hover:bg-[#5855E0] text-white rounded-lg transition-colors"
          >
            Connect Your First HardDrives
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {providers.map((provider) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              onSelect={() => setSelectedProvider(selectedProvider?.id === provider.id ? null : provider)}
              isSelected={selectedProvider?.id === provider.id}
              onRefresh={() => refreshProvider(provider.id)}
              onDelete={() => setShowDeleteModal(provider)}
              onTest={(toolName) => testProvider(provider.id, toolName)}
              isRefreshing={refreshingProvider === provider.id}
              isTesting={testingProvider === provider.id}
              testResult={testResults[provider.id]}
              formatDate={formatDate}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
          ))}
        </div>
      )}

      {/* Connect Modal */}
      <AnimatePresence>
        {showConnectModal && (
          <ConnectMCPModal
            onClose={() => setShowConnectModal(false)}
            onSuccess={() => {
              setShowConnectModal(false);
              fetchProviders();
            }}
            organizationId={activeOrganization.organization_id}
          />
        )}
      </AnimatePresence>

      {/* Trash Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <DeleteProviderModal
            provider={showDeleteModal}
            onClose={() => setShowDeleteModal(null)}
            onDelete={() => deleteProvider(showDeleteModal.id)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Helper function to detect if a tool has side effects (matches backend logic)
const toolHasSideEffects = (toolName, toolDescription = '') => {
  const nameLower = toolName.toLowerCase();
  
  // Safe patterns - never block
  const safePatterns = ['list_', 'get_', 'fetch_', 'read_', 'search_', 'echo', 'test_', 'mm.echo', 'mm.list', 'mm.get'];
  for (const safe of safePatterns) {
    if (nameLower.startsWith(safe) || nameLower.includes(safe)) {
      return false;
    }
  }
  
  // Side effect prefixes
  const sideEffectPrefixes = [
    'send_', 'create_', 'update_', 'delete_', 'write_', 'post_', 'put_', 'patch_',
    'email_', 'sms_', 'notify_', 'webhook_', 'calendar_', 'schedule_', 'book_',
    'submit_', 'publish_', 'upload_', 'insert_', 'remove_'
  ];
  
  for (const prefix of sideEffectPrefixes) {
    if (nameLower.includes(prefix)) return true;
  }
  
  // Side effect keywords at start
  const sideEffectKeywords = ['send', 'create', 'update', 'delete', 'write', 'email', 'sms', 'notify', 'webhook', 'book', 'submit', 'publish', 'upload', 'insert', 'remove'];
  for (const keyword of sideEffectKeywords) {
    if (nameLower.startsWith(keyword)) return true;
  }
  
  return false;
};

// Provider Card Component
const ProviderCard = ({ 
  provider, 
  onSelect, 
  isSelected, 
  onRefresh, 
  onDelete, 
  onTest,
  isRefreshing,
  isTesting,
  testResult,
  formatDate,
  searchTerm,
  setSearchTerm
}) => {
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'safe', 'side-effect'
  const [selectedTestTool, setSelectedTestTool] = useState('');
  
  // Get safe tools for test dropdown
  const safeTools = provider.discovered_tools?.filter(t => !toolHasSideEffects(t.name, t.description)) || [];
  
  // Filter tools based on search and filter mode
  const filteredTools = (() => {
    if (!isSelected) return provider.discovered_tools;
    
    let tools = provider.discovered_tools || [];
    
    // Apply search filter
    if (searchTerm) {
      tools = tools.filter(t => 
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply safety filter
    if (filterMode === 'safe') {
      tools = tools.filter(t => !toolHasSideEffects(t.name, t.description));
    } else if (filterMode === 'side-effect') {
      tools = tools.filter(t => toolHasSideEffects(t.name, t.description));
    }
    
    return tools;
  })();

  // Count safe vs side-effect tools
  const safeCount = provider.discovered_tools?.filter(t => !toolHasSideEffects(t.name, t.description)).length || 0;
  const sideEffectCount = (provider.discovered_tools?.length || 0) - safeCount;

  return (
    <div className="rounded-xl border border-[rgba(255,255,255,0.1)] overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 bg-[rgba(255,255,255,0.02)] flex items-center justify-between cursor-pointer hover:bg-[rgba(255,255,255,0.04)] transition-colors"
        onClick={onSelect}
      >
        <div className="flex items-center gap-4">
          <div className={`p-2 rounded-lg ${provider.status === 'active' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
            <HardDrives size={24} className={provider.status === 'active' ? 'text-green-400' : 'text-red-400'} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-white font-medium">{provider.name}</h3>
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                provider.status === 'active' 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {provider.status}
              </span>
            </div>
            <p className="text-sm text-[#A0AEC0] mt-0.5 flex items-center gap-2">
              <span className="truncate max-w-[300px]">{provider.server_url}</span>
              <span className="text-[rgba(255,255,255,0.3)]">•</span>
              <span>{safeCount} safe</span>
              {sideEffectCount > 0 && (
                <>
                  <span className="text-[rgba(255,255,255,0.3)]">•</span>
                  <span className="text-amber-400">{sideEffectCount} side-effect</span>
                </>
              )}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Test Tool Dropdown + Button */}
          <div className="flex items-center gap-1">
            <select
              value={selectedTestTool}
              onChange={(e) => { e.stopPropagation(); setSelectedTestTool(e.target.value); }}
              onClick={(e) => e.stopPropagation()}
              className="px-2 py-1.5 text-xs bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-l-lg text-white focus:outline-none focus:border-[#6366F1] max-w-[120px]"
              data-testid="test-tool-select"
            >
              <option value="">mm.echo</option>
              {safeTools.filter(t => t.name !== 'mm.echo').slice(0, 10).map(t => (
                <option key={t.name} value={t.name}>{t.name}</option>
              ))}
            </select>
            <button
              onClick={(e) => { e.stopPropagation(); onTest(selectedTestTool || 'mm.echo'); }}
              disabled={isTesting || provider.status !== 'active'}
              className="px-3 py-1.5 text-sm bg-[#6366F1]/20 text-[#6366F1] hover:bg-[#6366F1]/30 disabled:opacity-50 disabled:cursor-not-allowed rounded-r-lg transition-colors flex items-center gap-1.5"
              title="Run connectivity test with safe tool"
              data-testid="run-test-btn"
            >
              {isTesting ? (
                <CircleNotch size={14} className="animate-spin" />
              ) : (
                <Lightning size={14} />
              )}
              Test
            </button>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onRefresh(); }}
            disabled={isRefreshing}
            className="p-2 text-[#A0AEC0] hover:text-white hover:bg-[rgba(255,255,255,0.05)] rounded-lg transition-colors"
            title="Refresh tools"
          >
            <ArrowsClockwise size={18} className={isRefreshing ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            title="Disconnect"
          >
            <Trash size={18} />
          </button>
          {isSelected ? <CaretDown size={20} className="text-[#A0AEC0]" /> : <CaretRight size={20} className="text-[#A0AEC0]" />}
        </div>
      </div>

      {/* Expanded Tools List */}
      <AnimatePresence>
        {isSelected && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-[rgba(255,255,255,0.1)]"
          >
            <div className="p-4 space-y-4">
              {/* Test Result */}
              {testResult && (
                <div className={`p-3 rounded-lg border ${
                  testResult.success 
                    ? 'bg-green-500/10 border-green-500/30' 
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {testResult.success ? (
                      <CheckCircle size={16} className="text-green-400" />
                    ) : (
                      <XCircle size={16} className="text-red-400" />
                    )}
                    <span className={`font-medium ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>
                      {testResult.success ? 'Connection Test Passed' : 'Connection Test Failed'}
                    </span>
                    <span className="text-xs text-[#A0AEC0] ml-auto">
                      {testResult.latency_ms}ms
                    </span>
                  </div>
                  {testResult.success && testResult.echo_verified && (
                    <p className="text-xs text-[#A0AEC0]">
                      Echo verified: <code className="px-1 py-0.5 bg-[rgba(255,255,255,0.1)] rounded">{testResult.echoed_message?.substring(0, 50)}...</code>
                    </p>
                  )}
                  {!testResult.success && testResult.error && (
                    <p className="text-xs text-red-400">
                      {typeof testResult.error === 'string' ? testResult.error : testResult.error.message || JSON.stringify(testResult.error)}
                    </p>
                  )}
                </div>
              )}

              {/* Metadata */}
              <div className="flex gap-6 text-sm text-[#A0AEC0]">
                <div>
                  <span className="text-[rgba(255,255,255,0.5)]">Last checked:</span>{' '}
                  {formatDate(provider.last_checked_at)}
                </div>
                <div>
                  <span className="text-[rgba(255,255,255,0.5)]">Created:</span>{' '}
                  {formatDate(provider.created_at)}
                </div>
              </div>

              {provider.last_error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400">
                  <WarningCircle size={14} className="inline mr-2" />
                  {provider.last_error}
                </div>
              )}

              {/* MagnifyingGlass + Filter */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search tools..."
                    className="w-full pl-10 pr-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#6366F1]"
                  />
                </div>
                
                {/* Filter Tabs */}
                <div className="flex items-center gap-1 bg-[rgba(255,255,255,0.05)] rounded-lg p-1">
                  <button
                    onClick={() => setFilterMode('all')}
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${
                      filterMode === 'all' 
                        ? 'bg-[#6366F1] text-white' 
                        : 'text-[#A0AEC0] hover:text-white'
                    }`}
                  >
                    All ({provider.discovered_tools?.length || 0})
                  </button>
                  <button
                    onClick={() => setFilterMode('safe')}
                    className={`px-3 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                      filterMode === 'safe' 
                        ? 'bg-green-500 text-white' 
                        : 'text-[#A0AEC0] hover:text-white'
                    }`}
                  >
                    <Check size={12} />
                    Safe ({safeCount})
                  </button>
                  <button
                    onClick={() => setFilterMode('side-effect')}
                    className={`px-3 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                      filterMode === 'side-effect' 
                        ? 'bg-amber-500 text-white' 
                        : 'text-[#A0AEC0] hover:text-white'
                    }`}
                  >
                    <Warning size={12} />
                    Side-effect ({sideEffectCount})
                  </button>
                </div>
              </div>

              {/* Tools Grid */}
              <div className="grid gap-2 max-h-[400px] overflow-auto">
                {filteredTools?.length > 0 ? (
                  filteredTools.map((tool) => {
                    const hasSideEffects = toolHasSideEffects(tool.name, tool.description);
                    return (
                      <div 
                        key={tool.name}
                        className="p-3 rounded-lg bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.05)] hover:border-[rgba(255,255,255,0.1)] transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-white font-mono text-sm truncate">{tool.name}</p>
                              {hasSideEffects ? (
                                <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] rounded bg-amber-500/20 text-amber-400 flex items-center gap-1">
                                  <Warning size={10} />
                                  Side-effect
                                </span>
                              ) : (
                                <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] rounded bg-green-500/20 text-green-400 flex items-center gap-1">
                                  <Check size={10} />
                                  Safe
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-[#A0AEC0] mt-1 line-clamp-2">{tool.description || 'No description'}</p>
                          </div>
                          <Lightning size={14} className={hasSideEffects ? 'text-amber-400' : 'text-green-400'} />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-center text-[#A0AEC0] py-4">
                    {searchTerm ? 'No tools match your search' : 'No tools discovered'}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Connect MCP HardDrives Modal
const ConnectMCPModal = ({ onClose, onSuccess, organizationId }) => {
  const [preset, setPreset] = useState('custom');
  const [name, setName] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [token, setToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Preset configurations
  const PRESETS = {
    custom: {
      name: 'Custom',
      serverUrl: '',
      instructions: null
    },
    zapier: {
      name: 'Zapier MCP',
      serverUrl: 'https://actions.zapier.com/mcp',
      instructions: {
        title: 'Zapier MCP Setup',
        steps: [
          '1. Go to zapier.com and sign in to your account',
          '2. Navigate to "Developer" → "API Keys" in settings',
          '3. Create a new API key with MCP access enabled',
          '4. Copy the API key and paste it below as your Bearer Token',
          '5. Ensure your Zaps are configured to expose actions via MCP'
        ]
      }
    },
    make: {
      name: 'Make MCP',
      serverUrl: 'https://hook.make.com/mcp',
      instructions: {
        title: 'Make (Integromat) MCP Setup',
        steps: [
          '1. Log in to make.com and go to your organization settings',
          '2. Navigate to "API" → "Tokens" section',
          '3. Create a new API token with appropriate scopes',
          '4. Enable MCP endpoint access in your organization settings',
          '5. Copy the token and paste it below as your Bearer Token'
        ]
      }
    },
    n8n: {
      name: 'n8n MCP',
      serverUrl: 'https://your-n8n-instance.com/mcp',
      instructions: {
        title: 'n8n MCP Setup',
        steps: [
          '1. Ensure your n8n instance has the MCP plugin installed',
          '2. Go to Settings → API in your n8n dashboard',
          '3. Generate a new API key with workflow execution permissions',
          '4. Update the Server URL above with your n8n instance URL',
          '5. Copy the API key and paste it below as your Bearer Token'
        ]
      }
    }
  };

  // Handle preset change
  const handlePresetChange = (newPreset) => {
    setPreset(newPreset);
    const config = PRESETS[newPreset];
    if (config) {
      if (newPreset !== 'custom') {
        setName(config.name);
      } else {
        setName('');
      }
      setServerUrl(config.serverUrl);
    }
    setTestResult(null);
  };

  const handleConnect = async () => {
    if (!name.trim() || !serverUrl.trim() || !token.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setConnecting(true);
    setTestResult(null);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/integrations/mcp/providers`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Organization-Id': organizationId
          },
          body: JSON.stringify({
            name: name.trim(),
            server_url: serverUrl.trim(),
            token: token.trim()
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        setTestResult({
          success: true,
          message: data.message,
          toolCount: data.discovered_tools_count
        });
        toast.success('MCP Server connected successfully!');
        setTimeout(onSuccess, 1500);
      } else {
        setTestResult({
          success: false,
          message: data.detail || 'Connection failed'
        });
      }
    } catch (err) {
      setTestResult({
        success: false,
        message: 'Failed to connect to server'
      });
    } finally {
      setConnecting(false);
    }
  };

  const currentPreset = PRESETS[preset];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#1A1A2E] rounded-2xl border border-[rgba(255,255,255,0.1)] w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-[rgba(255,255,255,0.1)]">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Plug size={24} className="text-[#6366F1]" />
            Connect MCP HardDrives
          </h2>
          <p className="text-sm text-[#A0AEC0] mt-1">
            Connect to an external MCP server to access its tools
          </p>
        </div>

        <div className="p-6 space-y-4">
          {/* Preset Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[#A0AEC0]">Provider Preset</label>
            <select
              value={preset}
              onChange={(e) => handlePresetChange(e.target.value)}
              className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1]"
              data-testid="preset-select"
            >
              <option value="custom">Custom HardDrives</option>
              <option value="zapier">Zapier MCP</option>
              <option value="make">Make (Integromat) MCP</option>
              <option value="n8n">n8n MCP</option>
            </select>
          </div>

          {/* Setup Instructions for Presets */}
          {currentPreset?.instructions && (
            <div className="p-4 rounded-lg bg-[rgba(99,102,241,0.1)] border border-[rgba(99,102,241,0.3)]">
              <h4 className="text-sm font-medium text-[#6366F1] mb-2 flex items-center gap-2">
                <Info size={16} />
                {currentPreset.instructions.title}
              </h4>
              <ul className="space-y-1">
                {currentPreset.instructions.steps.map((step, idx) => (
                  <li key={idx} className="text-xs text-[#A0AEC0]">{step}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-medium text-[#A0AEC0]">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., My Zapier Integration"
              className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#6366F1]"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-[#A0AEC0]">HardDrives URL *</label>
            <input
              type="url"
              value={serverUrl}
              onChange={(e) => setServerUrl(e.target.value)}
              placeholder="https://mcp.example.com/mcp"
              className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#6366F1]"
            />
            <p className="text-xs text-[#A0AEC0]">The MCP endpoint URL (must support JSON-RPC 2.0)</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-[#A0AEC0]">Bearer Token *</label>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Enter your API token"
              className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#6366F1]"
            />
            <p className="text-xs text-[#A0AEC0]">Token will be encrypted and stored securely. Never stored in plaintext.</p>
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`p-4 rounded-lg border ${
              testResult.success 
                ? 'bg-green-500/10 border-green-500/30' 
                : 'bg-red-500/10 border-red-500/30'
            }`}>
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <CheckCircle size={20} className="text-green-400" />
                ) : (
                  <XCircle size={20} className="text-red-400" />
                )}
                <span className={testResult.success ? 'text-green-400' : 'text-red-400'}>
                  {testResult.message}
                </span>
              </div>
              {testResult.toolCount > 0 && (
                <p className="text-sm text-[#A0AEC0] mt-2 ml-7">
                  Discovered {testResult.toolCount} tools
                </p>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-[rgba(255,255,255,0.1)] flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[#A0AEC0] hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConnect}
            disabled={connecting || !name.trim() || !serverUrl.trim() || !token.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#5855E0] disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            {connecting && <CircleNotch size={18} className="animate-spin" />}
            {connecting ? 'Connecting...' : 'Connect & Test'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Trash Provider Modal
const DeleteProviderModal = ({ provider, onClose, onDelete }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete();
    setDeleting(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#1A1A2E] rounded-2xl border border-[rgba(255,255,255,0.1)] w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-red-500/10 rounded-full">
              <Warning size={24} className="text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Disconnect HardDrives</h2>
              <p className="text-sm text-[#A0AEC0]">This action cannot be undone</p>
            </div>
          </div>

          <p className="text-[#A0AEC0] mb-4">
            Are you sure you want to disconnect <span className="text-white font-medium">&ldquo;{provider.name}&rdquo;</span>?
            Any agent capability bindings using this provider will stop working.
          </p>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-[#A0AEC0] hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
            >
              {deleting && <CircleNotch size={18} className="animate-spin" />}
              Disconnect
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MCPProviders;
